import {
  AdminModel,
  DashboardContainerInterface,
  DatatableBuilderInterface,
  DatatableTypeInterface,
  PaginationRequest,
  PROVIDER_NAMES,
  textFiltrable,
} from '@ckmk/angular';
import { Injectable, inject } from '@angular/core';
import { PostModel } from '@app/src/app/modules/post/classes/default/model/post.model';
import {
  faCopy,
  faEye,
  faPenToSquare,
  faTrash,
  faTrashArrowUp,
} from '@fortawesome/free-solid-svg-icons';

@Injectable()
export class PostDatatableType implements DatatableTypeInterface {
  protected readonly container = inject<DashboardContainerInterface>(
    PROVIDER_NAMES.DASHBOARD_CONTAINER,
  );

  public constructor() {}

  public async configure(
    builder: DatatableBuilderInterface<AdminModel>,
  ): Promise<void> {
    const auth = this.container.authService.auth() as AdminModel;

    builder
      .setAuth(auth)
      .addIdentifyColumn()
      .addActiveColumn({
        editableOptions: {
          guard: {
            permissions: ['PERMISSION_UPDATE_POST'],
          },
        },
      })
      .add({
        type: 'file',
        name: 'cover',
        label: 'Cover',
        config: {
          editable: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_POST'],
            },
          },
        },
      })
      .add({
        type: 'file',
        name: 'picture',
        label: 'Picture',
        config: {
          editable: true,
          fixed: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_POST'],
            },
          },
        },
      })
      .add({
        type: 'translation',
        name: 'category',
        label: 'Category',
        config: {
          render: (data: PostModel): any => {
            return data.category?.title;
          },
        },
      })
      .add({
        type: 'translation',
        name: 'title',
        label: 'Title',
        config: {
          sortable: true,
          editable: true,
          fixed: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_POST'],
            },
          },
          filterable: textFiltrable({ translateAble: true }),
          inputs: {
            fieldConfig: {
              type: 'text',
            },
          },
        },
      })
      .add({
        type: 'text',
        name: 'slug',
        label: 'Slug',
        config: {
          sortable: true,
          editable: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_POST'],
            },
          },
          filterable: textFiltrable(),
        },
      })
      .addTimestampsColumns(auth.timezone)
      .addBlameColumns()
      .withPayload({
        filters: [],
        pagination: new PaginationRequest(),
        sorts: { createdAt: 'DESC' },
        includeCategory: true,
        includeCover: true,
        includePicture: true,
        includeSoftDeleted: true,
      })
      .withActions([
        {
          name: 'show',
          label: 'Show',
          icon: faEye,
          guard: {
            permissions: ['PERMISSION_READ_POST'],
          },
          activeColor: 'var(--color-warning)',
          path: (sc: PostModel): any => [
            this.container.path('/posts/show'),
            sc.id,
          ],
          filter: (sc: PostModel) => !sc.deletedAt,
        },
        {
          name: 'update',
          label: 'Update',
          icon: faPenToSquare,
          guard: {
            permissions: ['PERMISSION_UPDATE_POST'],
          },
          activeColor: 'var(--color-success)',
          path: (sc: PostModel): any => [
            this.container.path('/posts/update'),
            sc.id,
          ],
          filter: (sc: PostModel) => !sc.deletedAt,
        },
        {
          name: 'copy',
          label: 'Copy',
          icon: faCopy,
          guard: {
            permissions: ['PERMISSION_COPY_POST'],
          },
          activeColor: 'var(--color-primary)',
          path: (sc: PostModel): any => [
            this.container.path('/posts/copy'),
            sc.id,
          ],
          filter: (sc: PostModel) => !sc.deletedAt,
        },
        {
          name: 'delete',
          label: 'Delete',
          icon: faTrash,
          guard: {
            permissions: ['PERMISSION_DELETE_POST'],
          },
          activeColor: 'var(--color-danger)',
          filter: (sc: PostModel) => !sc.deletedAt,
        },
        {
          name: 'restore',
          label: 'Restore',
          icon: faTrashArrowUp,
          guard: {
            roles: ['ROLE_DEV'],
          },
          activeColor: 'var(--color-danger)',
          filter: (sc: PostModel) => !!sc.deletedAt,
        },
      ])
      .saveToStorage();
  }
}
